/**
 * Enhanced logging utility for React apps
 * 
 * Features:
 * - Automatically disables debug/log in production builds
 * - Supports log levels: DEBUG < INFO < WARN < ERROR
 * - Integrates with CloudWatch monitoring (errors are automatically tracked)
 * - Uses hostname-based environment detection
 * 
 * Usage:
 * ```typescript
 * import { logger } from '@mental-mint/shared-utils';
 * 
 * logger.debug('Debugging info');
 * logger.info('User action');
 * logger.warn('Warning message');
 * logger.error('Error occurred', error); // Automatically sent to CloudWatch
 * ```
 */

import { trackError } from '../SLAMonitoring';
import { getEnvironment } from './EnvironmentUtils';
import { ENVIRONMENTS } from './AnalyticsConstants';

// Use centralized environment detection
const isDevelopment = getEnvironment() === ENVIRONMENTS.DEVELOPMENT;

// Log levels (higher number = more important)
const LogLevel = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3
} as const;

// Current log level (DEBUG in dev, INFO in prod)
const currentLogLevel = isDevelopment ? LogLevel.DEBUG : LogLevel.INFO;

function shouldLog(level: number): boolean {
  return level >= currentLogLevel;
}

/**
 * Extract error information from console.error arguments
 */
function extractErrorInfo(args: any[]): { message: string; error?: Error } {
  // Find any Error object in arguments
  const errorArg = args.find(arg => arg instanceof Error);
  
  // Build message from all arguments
  const message = args
    .map(arg => {
      if (arg instanceof Error) return arg.message;
      if (typeof arg === 'string') return arg;
      if (arg === null) return 'null';
      if (arg === undefined) return 'undefined';
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    })
    .join(' ');

  return { message, error: errorArg };
}

export const logger = {
  /**
   * Debug level logging (only in development)
   * Not sent to monitoring
   */
  debug: (...args: any[]) => {
    if (shouldLog(LogLevel.DEBUG)) {
      console.debug('[DEBUG]', ...args);
    }
  },
  
  /**
   * Log level logging (only in development)
   * Not sent to monitoring
   */
  log: (...args: any[]) => {
    if (shouldLog(LogLevel.DEBUG)) {
      console.log('[LOG]', ...args);
    }
  },
  
  /**
   * Info level logging
   * Not sent to monitoring
   */
  info: (...args: any[]) => {
    if (shouldLog(LogLevel.INFO)) {
      console.info('[INFO]', ...args);
    }
  },
  
  /**
   * Warning level logging
   * Not sent to monitoring (warnings are not errors)
   */
  warn: (...args: any[]) => {
    if (shouldLog(LogLevel.WARN)) {
      console.warn('[WARN]', ...args);
    }
  },
  
  /**
   * Error level logging
   * Automatically sent to CloudWatch monitoring with generated error ID
   * 
   * @returns errorId - Unique identifier for tracking this error
   * 
   * Usage:
   *   logger.error('Something failed')
   *   logger.error('API call failed', error)
   *   const errorId = logger.error('Database error:', someData, error)
   */
  error: (...args: any[]): string => {
    if (shouldLog(LogLevel.ERROR)) {
      console.error('[ERROR]', ...args);
    }
    
    // Extract error information from arguments
    const { message, error } = extractErrorInfo(args);
    
    // Send to CloudWatch monitoring (wrapped in try-catch to prevent failures)
    // trackError() generates and returns the errorId
    try {
      const stack = error?.stack;
      return trackError(message, stack);
    } catch (trackingError) {
      // If monitoring fails, log to console but don't throw
      // This ensures the app continues working even if monitoring is broken
      console.warn('[Logger] Failed to send error to monitoring:', trackingError);
      // Return a fallback errorId so callers still get an ID
      return `errId_${Date.now()}_fallback`;
    }
  }
};

export default logger;
